import { useEffect } from 'react';
import type { ConversationGroup } from './conversationGroups.ts';

export interface ConversationInUrlInput {
  selection: ConversationGroup | null;
  /** Whether a channel's conversations are read as one, so no single one is being read. */
  merged: boolean;
  /** How many conversations, or channels, the person has; none leaves nothing to name. */
  groupCount: number;
  /** The conversation the URL names now. */
  linked: string | undefined;
  /** Renames it in place: choosing another conversation is not a place to come back to. */
  replaceLinked: (conversationId: string | undefined) => void;
}

/**
 * The URL names the conversation being read, so the page can be linked to as
 * it stands. Answers that conversation; undefined while a channel is read as one.
 */
export function useConversationInUrl({
  selection,
  merged,
  groupCount,
  linked,
  replaceLinked,
}: ConversationInUrlInput): string | undefined {
  const readingConversation =
    selection && !merged ? selection.conversations[0].id : undefined;

  useEffect(() => {
    if (groupCount === 0) return;
    if (linked === readingConversation) return;

    replaceLinked(readingConversation);
  }, [groupCount, readingConversation, linked, replaceLinked]);

  return readingConversation;
}
